import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faUser, faUsers, faExclamationTriangle, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';

const AssignFormation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Who this user may assign at all: chef_terrain only reaches superviseurs, resp_cati only reaches
  // enquêteurs CATI. The backend enforces the same rule; this keeps unreachable people out of the
  // picker and avoids calling person endpoints the role isn't allowed to read.
  const roleAllowsInterviewers = user?.role !== 'chef_terrain';
  const roleAllowsControllers = user?.role !== 'chef_terrain' && user?.role !== 'resp_cati';
  const roleAllowsSupervisors = user?.role !== 'resp_cati';

  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [interviewers, setInterviewers] = useState([]);
  const [controllers, setControllers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  
  const [selectedInterviewers, setSelectedInterviewers] = useState([]);
  const [selectedControllers, setSelectedControllers] = useState([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);
  const [dueDate, setDueDate] = useState('');

  // One search box per card — the lists run long enough that scrolling for a name is painful.
  const [interviewerSearch, setInterviewerSearch] = useState('');
  const [controllerSearch, setControllerSearch] = useState('');
  const [supervisorSearch, setSupervisorSearch] = useState('');
  
  // Track originally assigned people
  const [originallyAssignedInterviewers, setOriginallyAssignedInterviewers] = useState([]);
  const [originallyAssignedControllers, setOriginallyAssignedControllers] = useState([]);
  const [originallyAssignedSupervisors, setOriginallyAssignedSupervisors] = useState([]);
  
  // Modal state
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const showUnassignModalDismiss = useModalDismiss(() => setShowUnassignModal(false), { backdropClose: false, enabled: showUnassignModal });
  const [pendingUnassignments, setPendingUnassignments] = useState([]);

  useEffect(() => {
    fetchFormation();
    fetchPeople();
    fetchExistingAssignments();
  }, [id]);

  const fetchFormation = async () => {
    try {
      const response = await api.get(`/formations/${id}`);
      setFormation(response.data.data);
    } catch (error) {
      console.error('Error fetching formation:', error);
    } finally {
      setFetching(false);
    }
  };

  const fetchPeople = async () => {
    try {
      setError('');
      
      // Only request the lists this role is allowed to read — a 403 on one of them would
      // otherwise blank the whole picker.
      const [interviewersRes, controllersRes, supervisorsRes] = await Promise.all([
        roleAllowsInterviewers ? api.get('/interviewers') : Promise.resolve(null),
        roleAllowsControllers ? api.get('/controllers') : Promise.resolve(null),
        roleAllowsSupervisors ? api.get('/supervisors') : Promise.resolve(null),
      ]);
      
      // Extract data using the same pattern as SupervisorDashboard
      const extractPeople = (res) => {
        if (!res) return [];
        return res.data.success ? res.data.data : (Array.isArray(res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []));
      };

      const interviewersData = extractPeople(interviewersRes);
      const controllersData = extractPeople(controllersRes);
      const supervisorsData = extractPeople(supervisorsRes);
      
      // Filter out suspended people client-side
      const filteredInterviewers = Array.isArray(interviewersData)
        ? interviewersData.filter(i => i && i.status !== 'suspended' && (user?.role !== 'resp_cati' || i.is_cati))
        : [];
      const filteredControllers = Array.isArray(controllersData) ? controllersData.filter(c => c && c.status !== 'suspended') : [];
      const filteredSupervisors = Array.isArray(supervisorsData) ? supervisorsData.filter(s => s && s.status !== 'suspended') : [];
      
      setInterviewers(filteredInterviewers);
      setControllers(filteredControllers);
      setSupervisors(filteredSupervisors);
    } catch (error) {
      console.error('Error fetching people:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      setError(`Erreur lors du chargement des listes: ${error.response?.data?.message || error.message || 'Erreur inconnue'}`);
    }
  };

  const fetchExistingAssignments = async () => {
    try {
      const response = await api.get(`/formations/assignments?formation_id=${id}`);
      console.log('Assignments response:', response.data);
      
      // Get assignments array from response
      let assignments = [];
      if (response.data && response.data.success && response.data.data) {
        // If paginated, get data.data, otherwise just data
        assignments = Array.isArray(response.data.data) 
          ? response.data.data 
          : (response.data.data.data || []);
      }
      
      console.log('Parsed assignments:', assignments);
      
      // Pre-populate selected arrays with existing assignments
      const existingInterviewers = assignments
        .filter(a => a && a.person_type === 'interviewer' && a.person_id)
        .map(a => parseInt(a.person_id));
      
      const existingControllers = assignments
        .filter(a => a && a.person_type === 'controller' && a.person_id)
        .map(a => parseInt(a.person_id));
      
      const existingSupervisors = assignments
        .filter(a => a && a.person_type === 'supervisor' && a.person_id)
        .map(a => parseInt(a.person_id));
      
      console.log('Existing interviewers:', existingInterviewers);
      console.log('Existing controllers:', existingControllers);
      console.log('Existing supervisors:', existingSupervisors);
      
      setSelectedInterviewers(existingInterviewers);
      setSelectedControllers(existingControllers);
      setSelectedSupervisors(existingSupervisors);
      
      // Store original assignments for comparison
      setOriginallyAssignedInterviewers(existingInterviewers);
      setOriginallyAssignedControllers(existingControllers);
      setOriginallyAssignedSupervisors(existingSupervisors);
    } catch (error) {
      console.error('Error fetching existing assignments:', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const handleToggleInterviewer = (interviewerId) => {
    setSelectedInterviewers(prev =>
      prev.includes(interviewerId)
        ? prev.filter(id => id !== interviewerId)
        : [...prev, interviewerId]
    );
  };

  const handleToggleController = (controllerId) => {
    setSelectedControllers(prev =>
      prev.includes(controllerId)
        ? prev.filter(id => id !== controllerId)
        : [...prev, controllerId]
    );
  };

  const handleToggleSupervisor = (supervisorId) => {
    setSelectedSupervisors(prev =>
      prev.includes(supervisorId)
        ? prev.filter(id => id !== supervisorId)
        : [...prev, supervisorId]
    );
  };

  // Search matches on the fields people actually recognise someone by.
  const filterPeople = (people, term) => {
    const needle = term.trim().toLowerCase();
    if (!needle) return people;
    return people.filter(person =>
      [person.name, person.email, person.ref]
        .some(field => field && String(field).toLowerCase().includes(needle))
    );
  };

  const visibleInterviewers = filterPeople(interviewers, interviewerSearch);
  const visibleControllers = filterPeople(controllers, controllerSearch);
  const visibleSupervisors = filterPeople(supervisors, supervisorSearch);

  // Tout / Aucun apply to the people currently visible in the card: with a search active they add
  // or remove only the matches, leaving picks hidden by the search untouched.
  const handleSelectAll = (type) => {
    if (type === 'interviewer') {
      setSelectedInterviewers(prev => [...new Set([...prev, ...visibleInterviewers.map(i => i.id)])]);
    } else if (type === 'controller') {
      setSelectedControllers(prev => [...new Set([...prev, ...visibleControllers.map(c => c.id)])]);
    } else if (type === 'supervisor') {
      setSelectedSupervisors(prev => [...new Set([...prev, ...visibleSupervisors.map(s => s.id)])]);
    }
  };

  const handleDeselectAll = (type) => {
    if (type === 'interviewer') {
      const visibleIds = visibleInterviewers.map(i => i.id);
      setSelectedInterviewers(prev => prev.filter(id => !visibleIds.includes(id)));
    } else if (type === 'controller') {
      const visibleIds = visibleControllers.map(c => c.id);
      setSelectedControllers(prev => prev.filter(id => !visibleIds.includes(id)));
    } else if (type === 'supervisor') {
      const visibleIds = visibleSupervisors.map(s => s.id);
      setSelectedSupervisors(prev => prev.filter(id => !visibleIds.includes(id)));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for unassignments
    const unassignedInterviewers = originallyAssignedInterviewers.filter(
      id => !selectedInterviewers.includes(id)
    );
    const unassignedControllers = originallyAssignedControllers.filter(
      id => !selectedControllers.includes(id)
    );
    const unassignedSupervisors = originallyAssignedSupervisors.filter(
      id => !selectedSupervisors.includes(id)
    );
    
    const unassignments = [
      ...unassignedInterviewers.map(id => ({ person_type: 'interviewer', person_id: id })),
      ...unassignedControllers.map(id => ({ person_type: 'controller', person_id: id })),
      ...unassignedSupervisors.map(id => ({ person_type: 'supervisor', person_id: id })),
    ];
    
    // If there are unassignments, show warning modal
    if (unassignments.length > 0) {
      // Get names of people being unassigned
      const unassignedNames = [];
      unassignedInterviewers.forEach(id => {
        const person = interviewers.find(i => i.id === id);
        if (person) unassignedNames.push(`Enquêteur: ${person.name}`);
      });
      unassignedControllers.forEach(id => {
        const person = controllers.find(c => c.id === id);
        if (person) unassignedNames.push(`Contrôleur: ${person.name}`);
      });
      unassignedSupervisors.forEach(id => {
        const person = supervisors.find(s => s.id === id);
        if (person) unassignedNames.push(`Prestataire: ${person.name}`);
      });
      
      setPendingUnassignments(unassignments);
      setShowUnassignModal(true);
      return;
    }
    
    // No unassignments, proceed normally
    proceedWithAssignment(unassignments);
  };

  const proceedWithAssignment = async (unassignments = []) => {
    setLoading(true);
    setError('');
    setSuccess('');
    setShowUnassignModal(false);

    try {
      const assignments = [
        ...selectedInterviewers.map(id => ({ person_type: 'interviewer', person_id: id })),
        ...selectedControllers.map(id => ({ person_type: 'controller', person_id: id })),
        ...selectedSupervisors.map(id => ({ person_type: 'supervisor', person_id: id })),
      ];

      if (assignments.length === 0 && unassignments.length === 0) {
        setError('Veuillez sélectionner au moins une personne');
        setLoading(false);
        return;
      }

      const response = await api.post('/formations/assignments', {
        formation_id: id,
        assignments,
        unassignments: unassignments.length > 0 ? unassignments : undefined,
        due_date: dueDate || null,
      });

      setSuccess(response.data.message || 'Modification(s) effectuée(s) avec succès');
      setTimeout(() => {
        navigate(`/formations/${id}`);
      }, 1500);
    } catch (error) {
      console.error('Error assigning formation:', error);
      setError(error.response?.data?.message || 'Erreur lors de l\'assignation');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelUnassign = () => {
    setShowUnassignModal(false);
    setPendingUnassignments([]);
    // Restore original selections
    setSelectedInterviewers(originallyAssignedInterviewers);
    setSelectedControllers(originallyAssignedControllers);
    setSelectedSupervisors(originallyAssignedSupervisors);
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  // The formation's audience narrows the lists on top of the role restrictions above.
  const matchesAudience = (type) =>
    !formation?.target_audience || formation.target_audience === 'all' || formation.target_audience === type;

  const showInterviewers = roleAllowsInterviewers && matchesAudience('interviewer');
  const showControllers = roleAllowsControllers && matchesAudience('controller');
  const showSupervisors = roleAllowsSupervisors && matchesAudience('supervisor');

  // Role and audience can hide one or two of the three cards. A fixed 3-column grid would then
  // leave the remaining cards squeezed into a third of the page with dead space beside them, so
  // the columns follow how many cards actually render. Full literal class strings — Tailwind
  // purges anything it can't see in the source. Mobile stays single-column in every case.
  const visibleCardCount = [showInterviewers, showControllers, showSupervisors].filter(Boolean).length;
  const cardGridClass =
    visibleCardCount === 1 ? 'grid grid-cols-1 gap-6 max-w-2xl mx-auto' :
    visibleCardCount === 2 ? 'grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto' :
    'grid grid-cols-1 md:grid-cols-3 gap-6';

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      <div className="relative z-10 p-4 sm:p-6 animate-slide-up">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
              ASSIGNER LA FORMATION: {formation?.title}
            </h1>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Due date + actions — kept at the top so the lists below can be as long as they
                need to be without pushing Assigner off the screen. */}
            <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date limite (optionnel)
                  </label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  />
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 w-full md:w-auto">
                  <button
                    type="button"
                    onClick={() => navigate(`/formations/${id}`)}
                    className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Assignation...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        Assigner
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className={cardGridClass}>
              {/* Interviewers */}
              {showInterviewers && (
                <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center">
                      <FontAwesomeIcon icon={faUsers} className="mr-2 text-red-600" />
                      Enquêteurs ({selectedInterviewers.length})
                    </h2>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => handleSelectAll('interviewer')}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Tout
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeselectAll('interviewer')}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Aucun
                      </button>
                    </div>
                  </div>
                  <div className="relative mb-3">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                    />
                    <input
                      type="text"
                      value={interviewerSearch}
                      onChange={(e) => setInterviewerSearch(e.target.value)}
                      placeholder="Rechercher un enquêteur..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                    />
                  </div>
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {visibleInterviewers.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">Aucun résultat</p>
                    )}
                    {visibleInterviewers.map((interviewer) => (
                      <label
                        key={interviewer.id}
                        className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedInterviewers.includes(interviewer.id)}
                          onChange={() => handleToggleInterviewer(interviewer.id)}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">{interviewer.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Controllers */}
              {showControllers && (
                <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center">
                      <FontAwesomeIcon icon={faUsers} className="mr-2 text-red-600" />
                      Contrôleurs ({selectedControllers.length})
                    </h2>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => handleSelectAll('controller')}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Tout
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeselectAll('controller')}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Aucun
                      </button>
                    </div>
                  </div>
                  <div className="relative mb-3">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                    />
                    <input
                      type="text"
                      value={controllerSearch}
                      onChange={(e) => setControllerSearch(e.target.value)}
                      placeholder="Rechercher un contrôleur..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                    />
                  </div>
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {visibleControllers.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">Aucun résultat</p>
                    )}
                    {visibleControllers.map((controller) => (
                      <label
                        key={controller.id}
                        className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedControllers.includes(controller.id)}
                          onChange={() => handleToggleController(controller.id)}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">{controller.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Supervisors */}
              {showSupervisors && (
                <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center">
                      <FontAwesomeIcon icon={faUsers} className="mr-2 text-red-600" />
                      Prestataires ({selectedSupervisors.length})
                    </h2>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => handleSelectAll('supervisor')}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Tout
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeselectAll('supervisor')}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Aucun
                      </button>
                    </div>
                  </div>
                  <div className="relative mb-3">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                    />
                    <input
                      type="text"
                      value={supervisorSearch}
                      onChange={(e) => setSupervisorSearch(e.target.value)}
                      placeholder="Rechercher un prestataire..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                    />
                  </div>
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {visibleSupervisors.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">Aucun résultat</p>
                    )}
                    {visibleSupervisors.map((supervisor) => (
                      <label
                        key={supervisor.id}
                        className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSupervisors.includes(supervisor.id)}
                          onChange={() => handleToggleSupervisor(supervisor.id)}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">{supervisor.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </form>
        </div>
      </div>
      
      {/* Unassign Warning Modal */}
      {showUnassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 animate-slide-up">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-xl" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Confirmer la désassignation
                </h3>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Vous êtes sur le point de désassigner {pendingUnassignments.length} personne(s) de cette formation :
                </p>
                <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <ul className="space-y-1">
                    {pendingUnassignments.map((unassign, index) => {
                      let personName = 'N/A';
                      if (unassign.person_type === 'interviewer') {
                        const person = interviewers.find(i => i.id === unassign.person_id);
                        personName = person ? person.name : `ID: ${unassign.person_id}`;
                      } else if (unassign.person_type === 'controller') {
                        const person = controllers.find(c => c.id === unassign.person_id);
                        personName = person ? person.name : `ID: ${unassign.person_id}`;
                      } else if (unassign.person_type === 'supervisor') {
                        const person = supervisors.find(s => s.id === unassign.person_id);
                        personName = person ? person.name : `ID: ${unassign.person_id}`;
                      }
                      return (
                        <li key={index} className="text-sm text-gray-700">
                          • {personName} ({unassign.person_type === 'interviewer' ? 'Enquêteur' : unassign.person_type === 'controller' ? 'Contrôleur' : 'Prestataire'})
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <p className="text-sm text-red-600 mt-3 font-medium">
                  Cette action supprimera leur assignation à cette formation.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelUnassign}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={() => proceedWithAssignment(pendingUnassignments)}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Traitement...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignFormation;

